import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FiHome, FiUsers, FiChevronDown, FiHelpCircle, FiBell, FiSearch, FiCheck, FiSliders, FiUser, FiLogOut, FiGlobe, FiMenu, FiX, FiMail, FiFileText, FiMessageCircle, FiArrowRight, FiShare2, FiServer } from 'react-icons/fi';
import { IoMdMegaphone } from 'react-icons/io';
import ShieldCheckIcon from './ShieldCheckIcon';
import api from '../../api/api';
import { IGlobalState } from '../../model/IGlobalState';
import { CommunicationsCountsResult } from '../../model/CommunicationsCountsResult';
import { User } from '../../model/User';
import { startSetStock } from '../../actions/CurrentStock';
import NotificationsMenu from './notifications/NotificationsMenu';
import HelpPanel from './help/HelpPanel';
import TourHost from './tour/TourHost';
import { useNotifications, loadNotifications } from './notifications/notificationsStore';

interface Client { id: string; name: string; ticker: string; registry: string | null }

// "Communications ▾" menu (spec Dropdown / Communications 172:2966): the same sections as the Communications sub-tabs,
// each line from the tab counts; Social Posts has no count source, so it always shows its fallback line
const COMMS_ITEMS = [
    { path: '', label: 'Announcements', icon: <IoMdMegaphone />, line: (c: CommunicationsCountsResult) => `${c.announcements.toLocaleString()} published`, fallback: 'ASX announcements and their emails' },
    { path: 'email-campaigns', label: 'Email Campaigns', icon: <FiMail />, line: (c: CommunicationsCountsResult) => `${c.emailCampaigns.toLocaleString()} sent`, fallback: 'News updates to subscribers' },
    { path: 'contacts', label: 'Contacts', icon: <FiUsers />, line: (c: CommunicationsCountsResult) => `${c.contacts.toLocaleString()} subscribed`, fallback: 'Everyone on your email lists' },
    { path: 'email-templates', label: 'Email Templates', icon: <FiFileText />, line: (c: CommunicationsCountsResult) => `${c.emailTemplates.toLocaleString()} ${c.emailTemplates === 1 ? 'template' : 'templates'}`, fallback: 'Layouts for your emails' },
    { path: 'social-posts', label: 'Social Posts', icon: <FiShare2 />, fallback: 'Posts for LinkedIn and X' },
    { path: 'investor-questions', label: 'Investor Questions', icon: <FiMessageCircle />, line: (c: CommunicationsCountsResult) => `${c.investorQuestions.toLocaleString()} ${c.investorQuestions === 1 ? 'question' : 'questions'}`, fallback: 'Questions from the Website Hub' },
];

// last selected client per browser (cleared on sign-in); state.currentStock is the source of truth once loaded
export const CURRENT_TICKER_KEY = 'investi_current_stock_ticker';

export const initials = (s: string) => s.split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map(x => x[0].toUpperCase()).join('');

// profile name when set, else the sign-in email
export const displayName = (user: User) => [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username;

// profile photo, or initials when there is none
export const Avatar: React.FC<{ user: User; className?: string }> = ({ user, className = 'avatar' }) =>
    user.photoVersion
        ? <img className={`${className} photo`} src={api().getProfilePhotoUrl(user.photoVersion)} alt="" />
        : <span className={className}>{initials(displayName(user))}</span>;

const Header: React.FC = () => {
    const user = useSelector((state: IGlobalState) => state.user);
    const currentStock = useSelector((state: IGlobalState) => state.currentStock);
    const dispatch = useDispatch();
    const history = useHistory();
    const [switcherOpen, setSwitcherOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [navOpen, setNavOpen] = useState(false);
    const [commsOpen, setCommsOpen] = useState(false);
    const [commsCounts, setCommsCounts] = useState<CommunicationsCountsResult | null>(null);
    const { pathname } = useLocation();
    const closeMenus = () => { setSwitcherOpen(false); setMenuOpen(false); setNotifOpen(false); setNavOpen(false); setCommsOpen(false); };
    const closeHelp = useCallback(() => setHelpOpen(false), []);
    const unread = useNotifications().unreadCount;
    const [clients, setClients] = useState<Client[]>([]);
    const [search, setSearch] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);
    const selected = clients.find(x => x.id === currentStock?.id);

    const select = (c: Client) => {
        localStorage.setItem(CURRENT_TICKER_KEY, c.ticker);
        dispatch(startSetStock(c.ticker));
    };

    useEffect(() => {
        api().getClientDashboardResult().then(r => {
            const list: Client[] = r.stocks
                .map(s => ({ id: s.id, name: s.companyName || s.combinedTicker, ticker: s.combinedTicker, registry: s.registry }))
                .sort((a, b) => a.name.localeCompare(b.name));
            setClients(list);
            const stored = localStorage.getItem(CURRENT_TICKER_KEY);
            const initial = list.find(x => x.ticker === stored) ?? list.find(x => x.id === user.defaultStockId) ?? list[0];
            if (initial) select(initial);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const q = search.trim().toLowerCase();
    const visible = q ? clients.filter(x => x.name.toLowerCase().includes(q) || x.ticker.toLowerCase().includes(q)) : clients;

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
                setSwitcherOpen(false);
                setMenuOpen(false);
                setNotifOpen(false);
                setNavOpen(false);
                setCommsOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    // fresh counts each time the menu opens (cheap, and they change as people send)
    useEffect(() => {
        if (currentStock?.id) loadNotifications(currentStock.id).catch(() => undefined);
    }, [currentStock?.id]);

    useEffect(() => {
        if (!commsOpen || !currentStock?.id) return;
        api().getCommunicationsCounts(currentStock.id).then(setCommsCounts).catch(() => setCommsCounts(null));
    }, [commsOpen, currentStock?.id]);

    const openComms = (path: string) => {
        closeMenus();
        history.push(`/communications${path && `/${path}`}`);
    };
    const commsTab = pathname.startsWith('/communications') ? (pathname.split('/')[2] ?? '') : null;
    // the campaign / announcement subpages sit under their tabs
    const commsCurrent = commsTab === 'campaign' ? 'email-campaigns' : commsTab === 'announcement' ? '' : commsTab;

    const logout = async () => {
        await api().logout();
        window.location.href = '/';
    };

    return <header className="v2-header" ref={rootRef}>
        {/* spec "scrim": dims the page (not the nav) while a header dropdown is open; it sits inside rootRef, so it closes menus itself */}
        {(switcherOpen || menuOpen || notifOpen || navOpen || commsOpen) && <div className="nav-scrim" onClick={closeMenus} />}
        {/* tablet / mobile: the primary nav collapses behind this button (Header.scss) */}
        <button type="button" className="menu-toggle" aria-label="Menu" aria-expanded={navOpen}
            onClick={() => { setNavOpen(!navOpen); setSwitcherOpen(false); setMenuOpen(false); setNotifOpen(false); setCommsOpen(false); }}>
            {navOpen ? <FiX /> : <FiMenu />}
        </button>
        <Link to="/" className="logo"><img src="/images/login/logo-brand.svg" alt="Investi" /></Link>

        <nav className={`primary-nav${navOpen ? ' open' : ''}`} data-tour="primary-nav" onClick={() => setNavOpen(false)}>
            <NavLink to="/dashboard/overview" activeClassName="active"><FiHome /> Dashboard</NavLink>
            <NavLink to="/share-registry" activeClassName="active"><FiUsers /> Share Registry</NavLink>
            <div className="comms-nav">
                {/* stopPropagation: on tablet the nav's own click closes the collapsed menu */}
                <button type="button" className={`comms-nav-button${commsTab !== null ? ' active' : ''}`} aria-expanded={commsOpen}
                    onClick={e => { e.stopPropagation(); setCommsOpen(!commsOpen); setSwitcherOpen(false); setMenuOpen(false); setNotifOpen(false); }}>
                    <IoMdMegaphone /> Communications <FiChevronDown className="caret" />
                </button>
                {commsOpen && <div className="comms-menu">
                    {COMMS_ITEMS.map(x => <button key={x.path} type="button" className={x.path === commsCurrent ? 'current' : ''} onClick={() => openComms(x.path)}>
                        <span className="tile">{x.icon}</span>
                        <span className="text"><strong>{x.label}</strong><small>{commsCounts && x.line ? x.line(commsCounts) : x.fallback}</small></span>
                        <FiArrowRight className="go" />
                    </button>)}
                </div>}
            </div>
            <NavLink to="/website-hub" activeClassName="active"><FiGlobe /> Website Hub</NavLink>
        </nav>

        <div className="right">
            <div className="client-switcher">
                <button type="button" className="switcher-button" onClick={() => { setSwitcherOpen(!switcherOpen); setMenuOpen(false); setNotifOpen(false); setNavOpen(false); setCommsOpen(false); }}>
                    <span className="client-mark">{selected ? initials(selected.name) : ''}</span>
                    <span className="client-name">{selected?.name ?? 'No clients'}</span>
                    <FiChevronDown className="caret" />
                </button>
                {switcherOpen && <div className="switcher-menu">
                    <div className="menu-title">Switch client</div>
                    <div className="search"><FiSearch /><input placeholder="Search by name or ticker..." value={search} onChange={e => setSearch(e.target.value)} autoFocus /></div>
                    <ul>
                        {visible.map(x => <li key={x.id} className={x.id === selected?.id ? 'selected' : ''}
                            onClick={() => { select(x); setSwitcherOpen(false); setSearch(''); history.push('/dashboard/overview'); }}>
                            <span className="client-mark">{initials(x.name)}</span>
                            <span className="text">
                                <span className="name">{x.name}</span>
                                <span className="meta">{x.ticker}{x.registry && ` · ${x.registry}`}</span>
                            </span>
                            {x.id === selected?.id && <>
                                <span className="tick"><FiCheck /></span>
                                {/* stopPropagation: the row's own click goes to the dashboard */}
                                <button type="button" className="settings-link"
                                    onClick={e => { e.stopPropagation(); setSwitcherOpen(false); setSearch(''); history.push('/client-settings'); }}><FiSliders /> Settings</button>
                            </>}
                        </li>)}
                    </ul>
                </div>}
            </div>

            <button type="button" className="icon-button" title="Help & guides" onClick={() => { setHelpOpen(true); closeMenus(); }}><FiHelpCircle /></button>
            {helpOpen && <HelpPanel onClose={closeHelp} />}
            {/* guided tours: portalled to <body>, started on first visit or from the help panel (tour/TourHost.tsx) */}
            <TourHost />
            <div className="notif-bell">
                <button type="button" className="icon-button" title="Notifications" onClick={() => { if (!notifOpen && currentStock?.id) loadNotifications(currentStock.id).catch(() => undefined); setNotifOpen(!notifOpen); setSwitcherOpen(false); setMenuOpen(false); setNavOpen(false); setCommsOpen(false); }}>
                    <FiBell />
                    {unread > 0 && <span className="unread-badge">{unread}</span>}
                </button>
                {notifOpen && <NotificationsMenu onClose={() => setNotifOpen(false)} />}
            </div>

            <div className="avatar-menu">
                <button type="button" className="avatar-button" aria-label="Account menu" aria-haspopup="true" aria-expanded={menuOpen} onClick={() => { setMenuOpen(!menuOpen); setSwitcherOpen(false); setNotifOpen(false); setNavOpen(false); setCommsOpen(false); }}><Avatar user={user} /></button>
                {menuOpen && <div className="user-menu">
                    <div className="who">
                        <Avatar user={user} />
                        <div className="text">
                            <strong>{displayName(user)}</strong>
                            <span className="email">{user.username}</span>
                            {clients.length > 0 && <span className="manages">Manages {clients.length} {clients.length === 1 ? 'client' : 'clients'}</span>}
                        </div>
                    </div>
                    <div className="group">
                        <button type="button" onClick={() => { setMenuOpen(false); history.push('/account'); }}><FiUser /> My Profile</button>
                        <button type="button" onClick={() => { setMenuOpen(false); history.push('/account/security'); }}><ShieldCheckIcon /> Account &amp; Security <span className="meta">2FA on</span></button>
                        <button type="button" onClick={() => { setMenuOpen(false); history.push('/account/notifications'); }}><FiBell /> Notification Preferences</button>
                        <button type="button" onClick={() => { setMenuOpen(false); history.push('/account/mcp'); }}><FiServer /> MCP Server</button>
                    </div>
                    {/* spec: only for people who manage more than one company */}
                    {clients.length > 1 && <div className="group">
                        <button type="button" onClick={() => { setMenuOpen(false); history.push('/account/clients'); }}><FiUsers /> My Clients <span className="meta">{clients.length}</span></button>
                    </div>}
                    <div className="group">
                        <button type="button" className="sign-out" onClick={logout}><FiLogOut /> Sign Out</button>
                    </div>
                </div>}
            </div>
        </div>
    </header>;
};

export default Header;
